import { Spinner } from '@/components/glitch-ui/spinner';
import { useState, useMemo, useRef, forwardRef, useImperativeHandle } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { ColumnDef } from '@tanstack/react-table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Trash2, AlertCircle, Tag } from 'lucide-react';
import { useToast } from '@/hooks/useToast';
import { createAccountCallout, deleteAccountCallout, updateAccountCallout } from '@/lib/apis';
import { accountCalloutsQueryOptions, campaignCalloutsQueryOptions } from '@/lib/query-options';
import type { AccountCallout } from '@/lib/types';
import { CharacterCounter } from '@/components/ui/character-counter';
import { NoGoogleAdsAccountSelected } from '@/components/glitch-ui/no-google-ads-account';
import { DataTable } from '@/components/glitch-ui/data-table';
import { IconButton } from '@/components/glitch-ui/icon-button';
import { EditableField } from '@/components/glitch-ui/editable-field';
import { ConformationModal } from '@/components/glitch-ui/confirmation-modal';
import { FieldError, InputAdornment } from '@/components/glitch-ui/field-error';
import { googleAdsAccountAtom } from '@/store';
import type { AssetFilter } from './account-sitelinks';
import { getCampaignShortName } from '@/lib/utils';
import { CampaignPill } from '@/components/glitch-ui/campaign-pill';

// What a campaign is called in the Source column: the four-part Google name
// leads with the business, which repeats on every row and is exactly what
// survives truncation. Dropping it puts the distinguishing part first; the
// pill's tooltip keeps the whole name. Same rule as the sitelinks list.
const campaignSourceLabel = (name: string): string => {
  const parts = name.split(' | ');
  return parts.length >= 3 ? parts.slice(1).join(' | ') : name;
};

// A callout this account runs, whether set on the account or on campaigns
// (#6745). Only the account half was listed here, so the Source column always
// said "Account" whatever the truth was.
interface CalloutRow {
  key: string;
  text: string;
  /** Set for an account callout: the record this row can edit and delete. */
  account: AccountCallout | null;
  /** The campaigns carrying this callout. Empty for an account callout. */
  campaigns: { id: number; slug: string; name: string }[];
}

export interface AccountCalloutsRef {
  openAdd: () => void;
}

export const AccountCallouts = forwardRef<AccountCalloutsRef, { filter?: AssetFilter }>(
  ({ filter = {} }, ref) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const googleAdsAccount = useAtomValue(googleAdsAccountAtom);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [newCalloutText, setNewCalloutText] = useState('');
  // Tell-on-click (#5857): Create stays enabled; a click with an empty field marks it.
  const [newCalloutError, setNewCalloutError] = useState('');
  const [calloutToDelete, setCalloutToDelete] = useState<AccountCallout | null>(null);

  const {
    data: calloutsData,
    isLoading,
    isError,
  } = useQuery(accountCalloutsQueryOptions(googleAdsAccount?.googleads_account_id));

  // The campaigns' own callouts, so this list holds every callout the account
  // runs and not only the account-level half (#6745), the same as sitelinks.
  const { data: campaignCalloutsData } = useQuery(
    campaignCalloutsQueryOptions(googleAdsAccount?.googleads_account_id),
  );

  const createMutation = useMutation({
    mutationFn: (calloutText: string) => createAccountCallout(calloutText),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account-callouts'] });
      setIsAddDialogOpen(false);
      setNewCalloutText('');
      toast({
        title: 'Callout created',
        description: 'Account-level callout has been created and will apply to all campaigns.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to create callout',
        description: error.message || 'Please try again later.',
        variant: 'destructive',
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (customerAssetResourceName: string) => deleteAccountCallout(customerAssetResourceName),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account-callouts'] });
      setCalloutToDelete(null);
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to remove callout',
        description: error.message || 'Please try again later.',
        variant: 'destructive',
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ customerAssetResourceName, calloutText }: { customerAssetResourceName: string; calloutText: string }) =>
      updateAccountCallout(customerAssetResourceName, calloutText),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account-callouts'] });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to update callout',
        description: error.message || 'Please try again later.',
        variant: 'destructive',
      });
    },
  });

  const handleAddCallout = () => {
    // Tell-on-click (#5857): mark the field instead of toasts behind a disabled button.
    if (!newCalloutText.trim()) {
      setNewCalloutError('Required');
      return;
    }

    if (newCalloutText.length > 25) {
      setNewCalloutError('Max 25 characters');
      return;
    }

    createMutation.mutate(newCalloutText.trim());
  };

  const handleConfirmDelete = () => {
    if (calloutToDelete) {
      deleteMutation.mutate(calloutToDelete.customer_asset_resource_name);
    }
  };

  const handleCloseAddDialog = () => {
    setIsAddDialogOpen(false);
    setNewCalloutText('');
    setNewCalloutError('');
  };

  // Determine status pill colors
  const getStatusPillClasses = (status: string | null | undefined) => {
    if (!status) return 'bg-gray-100 text-gray-900';
    switch (status.toUpperCase().trim()) {
      case 'ENABLED':
        return 'bg-green-100 text-green-800';
      case 'HIDDEN':
        return 'bg-gray-100 text-gray-900';
      case 'REMOVED':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-900';
    }
  };

  // Format status text: trim and capitalize first letter
  const formatStatusText = (status: string | null | undefined) => {
    if (!status) return '';
    const trimmed = status.trim();
    if (!trimmed) return '';
    return trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
  };

  // Ref for stable column cell renderer (prevents DataTable cell remount on parent re-render)
  const updateMutateRef = useRef(updateMutation.mutate);
  updateMutateRef.current = updateMutation.mutate;

  const rows = useMemo((): CalloutRow[] => {
    const accountRows: CalloutRow[] = (calloutsData?.callouts ?? []).map((c) => ({
      key: `account-${c.customer_asset_resource_name}`,
      text: c.callout_text || '',
      account: c,
      campaigns: [],
    }));
    const campaignRows: CalloutRow[] = (campaignCalloutsData?.callouts ?? []).map((c) => ({
      key: `campaign-${c.id}`,
      text: c.text,
      account: null,
      campaigns: c.campaigns.map((camp) => ({
        id: camp.id,
        slug: camp.slug,
        name: getCampaignShortName(camp),
      })),
    }));
    return [...accountRows, ...campaignRows];
  }, [calloutsData, campaignCalloutsData]);

  // The toolbar's filter decides what shows: where the callout is set (#6745).
  const sourcePicked = filter.source ?? [];
  const visibleRows = useMemo(() => {
    if (sourcePicked.length === 0) return rows;
    return rows.filter((row) =>
      row.account ? sourcePicked.includes('account') : row.campaigns.some((c) => sourcePicked.includes(String(c.id))),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, sourcePicked.join('|')]);

  // The heading counts the rows on screen. Not "n/20": that limit is the
  // ACCOUNT's, the campaign rows are not subject to it, and Add Callout
  // already disables at 20.
  const calloutCount = visibleRows.length;

  const columns = useMemo((): ColumnDef<CalloutRow>[] => [
    {
      accessorKey: 'callout_text',
      header: `Callouts (${calloutCount})`,
      meta: { className: 'px-6 w-[40%] whitespace-normal align-top' },
      cell: ({ row }) => {
        const { account, text } = row.original;
        // A campaign's callout is edited on that campaign, so this row shows
        // it and nothing more.
        // px-2 py-1.5 is EditableField's own textarea padding: a read-only
        // row has to start where an editable one's text starts, or the column
        // has two left edges.
        if (!account) return <span className="block px-2 py-1.5 text-sm text-gray-900">{text}</span>;
        return (
          <EditableField
            value={text}
            onChange={(value) => {
              if (value.trim()) {
                updateMutateRef.current({
                  customerAssetResourceName: account.customer_asset_resource_name,
                  calloutText: value.trim(),
                });
              }
            }}
            maxLength={25}
            placeholder="Enter callout text"
            hideBullet
          />
        );
      },
    },
    {
      accessorKey: 'status',
      header: 'Status',
      meta: { className: 'px-6 w-[35%] align-top' },
      cell: ({ row }) => {
        const status = row.original.account?.status;
        return status ? (
          <span className={`inline-flex items-center justify-center whitespace-nowrap px-3.5 py-0.5 rounded-full text-xs font-medium ${getStatusPillClasses(status)}`}>
            {formatStatusText(status)}
          </span>
        ) : (
          <span className="text-sm font-medium text-gray-900">-</span>
        );
      },
    },
    {
      id: 'source',
      header: 'Source',
      enableSorting: false,
      meta: { className: 'px-6 w-[15%] whitespace-normal align-top' },
      // Where the callout is set: the account, so it applies to every campaign,
      // or the campaigns that carry it, each as the house pill.
      cell: ({ row }) =>
        row.original.campaigns.length === 0 ? (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-600">
            Account
          </span>
        ) : (
          <div className="flex flex-col items-start gap-1">
            {row.original.campaigns.map((c) => (
              <CampaignPill
                key={c.id}
                slug={c.slug}
                name={campaignSourceLabel(c.name)}
                title={c.name}
                hash="assets"
                className="max-w-[200px]"
              />
            ))}
          </div>
        ),
    },
    {
      id: 'actions',
      enableSorting: false,
      cell: ({ row }) =>
        row.original.account ? (
          <div className="flex justify-end">
            <IconButton
              variant="destructive"
              aria-label="Delete callout"
              onClick={() => setCalloutToDelete(row.original.account)}
            >
              <Trash2 className="h-4 w-4" aria-hidden="true" />
            </IconButton>
          </div>
        ) : null,
      meta: { className: 'px-6 w-[10%] text-right align-top' },
    },
  ], [calloutCount]);

  useImperativeHandle(ref, () => ({ openAdd: () => setIsAddDialogOpen(true) }), []);

  if (!googleAdsAccount?.googleads_account_id) {
    return (
      <NoGoogleAdsAccountSelected description="A Google Ads account needs to be selected for this organization before account callouts can be managed." />
    );
  }

  const callouts = visibleRows;

  // Error state
  if (isError) {
    return (
      <div className="bg-white shadow-sm border border-red-200 rounded-lg overflow-hidden">
        <div className="p-6 flex items-center gap-3 text-red-600">
          <AlertCircle className="h-5 w-5" />
          <span className="text-sm">Failed to load account callouts</span>
        </div>
      </div>
    );
  }

  // Loading state (show during initial load or refetch)
  if (isLoading) {
    return (
      <div className="border rounded-lg bg-white overflow-hidden">
        <div className="flex items-center justify-center gap-3 py-16">
          <Spinner size="md" className="text-gray-500" />
          <span className="text-gray-500">Loading account callouts...</span>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Table */}
      <DataTable
        data={callouts}
        columns={columns}
        variant="flush"
        pageSize={999}
        getRowId={(row) => row.key}
        emptyIcon={Tag}
        emptyTitle="No Account Callouts"
        emptyDescription="Create your first account callout to apply it to all campaigns by default."
      />

      {/* Add Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Add Account Callout</DialogTitle>
            <DialogDescription>
              This callout will apply to all campaigns by default. You can override it at the campaign level.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="callout-text-add">Callout Text</Label>
                <FieldError message={newCalloutError} />
              </div>
              <div className="relative">
                <Input
                  id="callout-text-add"
                  value={newCalloutText}
                  onChange={(e) => { setNewCalloutText(e.target.value); setNewCalloutError(''); }}
                  maxLength={25}
                  placeholder="Enter callout text (max 25 characters)"
                  error={!!newCalloutError}
                  className={newCalloutError ? 'pr-20' : 'pr-14'}
                />
                <InputAdornment error={!!newCalloutError}>
                  <CharacterCounter current={newCalloutText.length} max={25} variant="compact" />
                </InputAdornment>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleCloseAddDialog}
              disabled={createMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleAddCallout}
              disabled={createMutation.isPending}
              className="bg-[#290BD9] hover:bg-[#2309C5] text-white"
            >
              {createMutation.isPending ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <ConformationModal
        isOpen={!!calloutToDelete}
        onClose={() => setCalloutToDelete(null)}
        onConfirm={handleConfirmDelete}
        title="Remove Account Callout"
        description={`Are you sure you want to remove "${calloutToDelete?.callout_text}"? This will remove it from all campaigns that inherit it.`}
        confirmText="Remove"
        cancelText="Cancel"
        isLoading={deleteMutation.isPending}
      />
      </div>
    );
  },
);
AccountCallouts.displayName = 'AccountCallouts';
